/**
 * Fiscal calendar settings — the one-per-company FiscalCalendar row
 * that the resolver consults for fiscal bucketing.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { reportingAPI, type FiscalCalendar } from '../../../lib/api/reporting';

const MONTHS = [
  { value: 1,  label: 'January' },
  { value: 2,  label: 'February' },
  { value: 3,  label: 'March' },
  { value: 4,  label: 'April' },
  { value: 5,  label: 'May' },
  { value: 6,  label: 'June' },
  { value: 7,  label: 'July' },
  { value: 8,  label: 'August' },
  { value: 9,  label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

interface FiscalCalendarDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FiscalCalendarDialog({ open, onOpenChange }: FiscalCalendarDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<FiscalCalendar | null>(null);
  const [startMonth, setStartMonth] = useState(1);
  const [label, setLabel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await reportingAPI.listFiscalCalendars();
      const first = response.data.results[0] || null;
      setExisting(first);
      setStartMonth(first?.start_month || 1);
      setLabel(first?.name || '');
    } catch {
      toast.error('Failed to load fiscal calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  const save = async () => {
    setSaving(true);
    try {
      if (existing) {
        const response = await reportingAPI.updateFiscalCalendar(existing.id, {
          start_month: startMonth,
          name: label,
        });
        setExisting(response.data);
        toast.success('Fiscal calendar updated');
      } else {
        const response = await reportingAPI.createFiscalCalendar({
          start_month: startMonth,
          name: label,
        });
        setExisting(response.data);
        toast.success('Fiscal calendar created');
      }
    } catch {
      toast.error('Failed to save fiscal calendar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Fiscal calendar</DialogTitle>
          <DialogDescription>
            Sets the month your fiscal year begins. Used when a chart groups by
            Fiscal Month / Quarter / Year.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Label (optional)</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. FY2026"
              />
            </div>
            <div className="space-y-1">
              <Label>Fiscal year starts in</Label>
              <Select
                value={String(startMonth)}
                onValueChange={(v) => setStartMonth(parseInt(v, 10))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Non-January fiscal years currently fall back to calendar-year bucketing
                in the resolver — full offset support is planned.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Close
          </Button>
          <Button onClick={() => void save()} disabled={loading || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
